/**
 * BotTrafficDetectionPage
 *
 * Bot-score distribution chart (inline green SVG bars) + flagged-clicks list
 * (ip, ua, score, reason) + sensitivity slider + recent-actions.
 *
 * GREEN-only accent palette. No emerald/blue/orange/purple/dark/emoji.
 */
import { useMemo, useState } from "react";
import { useBotTrafficDetectionList } from "./hooks";
import { bucketScores, decodeEvent, flaggedByThreshold, type DecodedBotEvent } from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const H1 = "text-3xl font-semibold text-green-700";
const H2 = "text-base font-semibold text-zinc-900";

const CHART_W = 480;
const CHART_H = 160;
const BAR_GAP = 4;

function fmtTime(unixSec: number): string {
	if (!unixSec) return "-";
	const d = new Date(unixSec * 1000);
	return d.toISOString().replace("T", " ").slice(0, 19);
}

function fmtPct(n: number): string {
	return `${Math.round(n * 100)}%`;
}

export function BotTrafficDetectionPage() {
	const listQuery = useBotTrafficDetectionList();
	const [threshold, setThreshold] = useState<number>(0.5);

	const events: DecodedBotEvent[] = useMemo(() => {
		const rows = listQuery.data?.items ?? [];
		return rows.map(decodeEvent);
	}, [listQuery.data]);

	const buckets = useMemo(() => bucketScores(events), [events]);
	const maxCount = useMemo(() => Math.max(1, ...buckets), [buckets]);
	const flagged = useMemo(() => flaggedByThreshold(events, threshold), [events, threshold]);

	const barW = (CHART_W - BAR_GAP * (buckets.length - 1)) / buckets.length;

	return (
		<main
			data-testid="bot-traffic-detection-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					bot-traffic-detection
				</p>
				<h1 className={H1}>Bot traffic detection</h1>
				<p className="text-sm text-zinc-600">
					Score distribution, flagged clicks, and recent actions.
				</p>
			</header>

			<section className={CARD} aria-label="Score distribution">
				<div className="flex items-center justify-between">
					<h2 className={H2}>Score distribution</h2>
					<span className="text-xs text-zinc-500">
						{events.length} event{events.length === 1 ? "" : "s"}
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="bot-traffic-detection-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : events.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="bot-traffic-detection-empty"
					>
						No events yet.
					</p>
				) : (
					<div className="mt-4" data-testid="bot-traffic-detection-chart">
						<svg
							viewBox={`0 0 ${CHART_W} ${CHART_H + 24}`}
							className="h-auto w-full max-w-[640px]"
							role="img"
							aria-label="bot score distribution histogram"
						>
							{buckets.map((count, i) => {
								const h = (count / maxCount) * CHART_H;
								const x = i * (barW + BAR_GAP);
								const y = CHART_H - h;
								return (
									<g key={i} data-testid={`bot-traffic-detection-bar-${i}`}>
										<rect
											x={x}
											y={y}
											width={barW}
											height={h}
											className="fill-green-600"
											rx={2}
										/>
										<text
											x={x + barW / 2}
											y={CHART_H + 14}
											textAnchor="middle"
											className="fill-zinc-500 text-[10px]"
										>
											{(i / 10).toFixed(1)}
										</text>
									</g>
								);
							})}
						</svg>
						<p className="mt-1 text-xs text-zinc-500">
							x: bot score (0.0 clean to 1.0 bot) &nbsp;&middot;&nbsp; y: count
						</p>
					</div>
				)}
			</section>

			<section className={CARD} aria-label="Sensitivity">
				<h2 className={H2}>Sensitivity</h2>
				<p className="mt-1 text-xs text-zinc-500">
					Flag events scoring at or above this threshold.
				</p>
				<div className="mt-3 flex items-center gap-4">
					<input
						type="range"
						min={0}
						max={1}
						step={0.05}
						value={threshold}
						onChange={(e) => setThreshold(Number(e.currentTarget.value))}
						className="h-2 w-full max-w-md cursor-pointer appearance-none rounded-full bg-zinc-200 accent-green-600"
						data-testid="bot-traffic-detection-sensitivity"
						aria-label="Sensitivity threshold"
					/>
					<span
						className="w-14 rounded-[6px] border border-green-200 bg-green-50 px-2 py-1 text-center text-xs font-mono text-green-800"
						data-testid="bot-traffic-detection-threshold-value"
					>
						{threshold.toFixed(2)}
					</span>
				</div>
				<p className="mt-2 text-xs text-zinc-600" data-testid="bot-traffic-detection-flagged-count">
					Flagged: {flagged.length} of {events.length}
				</p>
			</section>

			<section className={CARD} aria-label="Flagged clicks">
				<h2 className={H2}>Flagged clicks</h2>
				{flagged.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="bot-traffic-detection-flagged-empty"
					>
						No clicks above threshold.
					</p>
				) : (
					<div className="mt-3 overflow-x-auto">
						<table className="w-full text-left text-sm" data-testid="bot-traffic-detection-flagged-table">
							<thead>
								<tr className="border-b border-zinc-200 text-xs uppercase text-zinc-500">
									<th className="py-2 pr-4">IP</th>
									<th className="py-2 pr-4">User agent</th>
									<th className="py-2 pr-4">Score</th>
									<th className="py-2">Reason</th>
								</tr>
							</thead>
							<tbody>
								{flagged.map((e) => (
									<tr
										key={e.id}
										className="border-b border-zinc-100"
										data-testid={`bot-traffic-detection-flagged-row-${e.id}`}
									>
										<td className="py-2 pr-4 font-mono text-xs text-zinc-800">{e.ip}</td>
										<td className="py-2 pr-4 text-xs text-zinc-700">
											<span className="block max-w-xs truncate" title={e.ua}>
												{e.ua}
											</span>
										</td>
										<td className="py-2 pr-4">
											<span className="rounded-[6px] border border-green-300 bg-green-50 px-2 py-0.5 font-mono text-xs text-green-800">
												{fmtPct(e.score)}
											</span>
										</td>
										<td className="py-2 text-xs text-zinc-700">{e.reason}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			<section className={CARD} aria-label="Recent actions">
				<h2 className={H2}>Recent actions</h2>
				{events.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="bot-traffic-detection-recent-empty"
					>
						No recent actions.
					</p>
				) : (
					<ul className="mt-3 divide-y divide-zinc-100" data-testid="bot-traffic-detection-recent">
						{events.slice(0, 10).map((e) => (
							<li
								key={e.id}
								className="flex items-center justify-between gap-3 py-2 text-xs"
								data-testid={`bot-traffic-detection-recent-${e.id}`}
							>
								<span className="font-mono text-zinc-600">{fmtTime(e.created_at)}</span>
								<span className="truncate text-zinc-700">{e.ip}</span>
								<span className="font-mono text-green-700">{fmtPct(e.score)}</span>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default BotTrafficDetectionPage;
